import { useState, useEffect, useCallback } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Box,
  Button,
  Flex,
  Text,
  Wrap,
  Tag,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Badge,
  Icon,
  Link,
} from '@chakra-ui/react'
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@chakra-ui/icons'
import { useLazyQuery } from '@apollo/client'
import {
  SCHOOLS_EXPORT_PAGE_QUERY,
  ACCOUNT_FIELDS,
  PAGE_SIZE,
} from 'src/components/FullDashboardCell/schoolExportShared'
import SchoolCsvDownloadMenu from 'src/components/FullDashboardCell/SchoolCsvDownloadMenu'

const SchoolExportRow = ({ school }) => {
  const sites = school.sites || []

  return (
    <Box
      p={3}
      bg="white"
      borderRadius="md"
      border="1px solid"
      borderColor="gray.200"
    >
      <Flex align="center" gap={2} flexWrap="wrap" mb={2}>
        <Text fontWeight={600} fontSize="sm" noOfLines={1}>
          {school.schoolName || 'Unnamed school'}
        </Text>
        {school.package && (
          <Badge colorScheme="teal" variant="subtle" fontSize="0.65rem">
            {school.package}
          </Badge>
        )}
        {school.usedForTesting && (
          <Badge colorScheme="orange" variant="subtle" fontSize="0.65rem">
            Test
          </Badge>
        )}
      </Flex>
      <Text fontSize="xs" color="gray.500" mb={2}>
        ID: {school.id}
      </Text>

      <Text fontSize="xs" fontWeight={600} color="gray.500" mb={1.5}>
        Sites
      </Text>
      {sites.length === 0 ? (
        <Text fontSize="xs" color="gray.400" mb={3}>
          No sites
        </Text>
      ) : (
        <Flex direction="column" gap={1} mb={3}>
          {sites.map((site) => (
            <Flex key={site.id} align="baseline" gap={2} flexWrap="wrap">
              {site.isPrivate || !site.hostname ? (
                <Text fontSize="sm" color="gray.800">
                  {site.label || 'Untitled site'}
                </Text>
              ) : (
                <Link
                  href={`https://${site.hostname}`}
                  isExternal
                  fontSize="sm"
                  color="teal.600"
                >
                  {site.label || site.hostname}
                </Link>
              )}
              {site.hostname && (
                <Text fontSize="xs" color="gray.500">
                  {site.hostname}
                  {site.isPrivate ? ' · private' : ''}
                </Text>
              )}
            </Flex>
          ))}
        </Flex>
      )}

      <Text fontSize="xs" fontWeight={600} color="gray.500" mb={1.5}>
        Account features
      </Text>
      <Wrap spacing={1.5}>
        {ACCOUNT_FIELDS.map(({ key, label }) => (
          <Tag
            key={key}
            size="sm"
            variant="outline"
            colorScheme={school[key] ? 'teal' : 'gray'}
            borderRadius="md"
          >
            {label}: {school[key] ? 'yes' : 'no'}
          </Tag>
        ))}
      </Wrap>
    </Box>
  )
}

const LoadingRows = () => (
  <Flex direction="column" gap={2}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Box key={i} p={3} border="1px solid" borderColor="gray.100" borderRadius="md">
        <Skeleton height="14px" width="40%" mb={2} />
        <Skeleton height="10px" width="60%" />
      </Box>
    ))}
  </Flex>
)

const SchoolExportModal = ({
  isOpen,
  onClose,
  packages,
  onDownload,
  downloading,
}) => {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [result, setResult] = useState(null)

  const [fetchPage, { loading }] = useLazyQuery(SCHOOLS_EXPORT_PAGE_QUERY, {
    fetchPolicy: 'cache-first',
    onCompleted: (data) => {
      setResult(data.schoolsExportPage)
    },
  })

  const loadPage = useCallback(
    (nextPage, nextSearch) => {
      fetchPage({
        variables: {
          page: nextPage,
          limit: PAGE_SIZE,
          search: nextSearch || null,
        },
      })
    },
    [fetchPage]
  )

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    if (!isOpen) return
    setPage(1)
    loadPage(1, debouncedSearch)
  }, [isOpen, debouncedSearch, loadPage])

  useEffect(() => {
    if (!isOpen) {
      setSearch('')
      setDebouncedSearch('')
      setPage(1)
      setResult(null)
    }
  }, [isOpen])

  const handlePageChange = (nextPage) => {
    setPage(nextPage)
    loadPage(nextPage, debouncedSearch)
  }

  const items = result?.items || []
  const total = result?.total || 0
  const start = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const end = Math.min(page * PAGE_SIZE, total)
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const isInitialLoad = loading && !result
  const isPaginating = loading && !!result

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.400" />
      <ModalContent maxW="90vw" maxH="90vh">
        <ModalHeader borderBottom="1px solid" borderColor="gray.100" py={4}>
          <Text fontSize="lg" fontWeight={600}>
            School export preview
          </Text>
          <Text fontSize="sm" color="gray.500" fontWeight={400} mt={1}>
            Browse schools with package and account details. Dormant schools
            are excluded. Test schools are included.
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={4}>
          <InputGroup mb={4} size="md">
            <InputLeftElement pointerEvents="none">
              <Icon as={SearchIcon} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search by name, subdomain, acronym, or ID..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
            />
          </InputGroup>

          {isInitialLoad ? (
            <LoadingRows />
          ) : items.length === 0 ? (
            <Flex
              direction="column"
              align="center"
              justify="center"
              py={16}
              color="gray.500"
            >
              <Text fontSize="sm">No schools match your search.</Text>
            </Flex>
          ) : (
            <Box opacity={isPaginating ? 0.55 : 1} transition="opacity 0.15s">
              <Flex direction="column" gap={2}>
                {items.map((school) => (
                  <SchoolExportRow key={school.id} school={school} />
                ))}
              </Flex>
            </Box>
          )}
        </ModalBody>

        <ModalFooter
          borderTop="1px solid"
          borderColor="gray.100"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={3}
        >
          <Flex align="center" gap={2}>
            <Button
              size="sm"
              variant="outline"
              leftIcon={<ChevronLeftIcon />}
              onClick={() => handlePageChange(page - 1)}
              isDisabled={page <= 1 || loading}
            >
              Previous
            </Button>
            <Text fontSize="sm" color="gray.600" minW="140px" textAlign="center">
              {total > 0 ? `${start}–${end} of ${total}` : '0 schools'}
            </Text>
            <Button
              size="sm"
              variant="outline"
              rightIcon={<ChevronRightIcon />}
              onClick={() => handlePageChange(page + 1)}
              isDisabled={page >= totalPages || loading}
            >
              Next
            </Button>
          </Flex>

          <SchoolCsvDownloadMenu
            packages={packages}
            onDownload={onDownload}
            downloading={downloading}
            size="sm"
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default SchoolExportModal
